import { FC, useEffect, useState, useRef } from 'react';
import { Input, Popover, Box } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';

import { IconCalendar } from '@tabler/icons-react';
import { DateRange, DateRangeDropdown } from './Dropdown';
import { quickRanges as defaultQuickRanges } from 'constants/reporting';
import { getDateRange, formatUrlDateWithTimezone } from 'utils/date';
import { getInputRangeLabel } from 'pages/reporting/utils';
import { IQuickRange } from './IQuickRange';

interface DateRangeFilterProps {
  /** Called when the date range is applied */
  applyFilter: (dateRange: DateRange) => void;
  /** List of quick ranges to show (optional) */
  quickRanges?: IQuickRange[];
  /** Maximum selectable date (optional) */
  maxDate?: Date;
  /** Hide the quick search UI (optional) */
  hideQuickSearch?: boolean;
  /** Prefix for URL search params (optional) */
  paramPrefix?: string;
  /** Default custom value for quick range (optional) */
  defaultCustomValue?: string;
  /** Controlled value for the date range (optional) */
  value?: DateRange;
  /** Disable time selection and formatting (optional, default true) */
  disableTime?: boolean;
}

const getCustomRange = (customValue: string, quickRanges: IQuickRange[]) => {
  const range = quickRanges.find((range) => range.customValue === customValue);
  return range;
};

const DateRangeFilter: FC<DateRangeFilterProps> = ({ applyFilter, quickRanges = defaultQuickRanges, maxDate, hideQuickSearch, paramPrefix = '', defaultCustomValue = '30d', value, disableTime = true }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [opened, setOpened] = useState(false);
  const [customValue, setCustomValue] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(value);
  const lastStartDateParam = useRef<string | null>(null);
  const lastEndDateParam = useRef<string | null>(null);
  const lastCustomValueParam = useRef<string | null>(null);
  const initialized = useRef(false);

  const formatDateRangeValue = () => {
    const range = getCustomRange(customValue || '', quickRanges);
    if (range) {
      return range.label;
    }
    if (dateRange?.from && dateRange?.to) {
      return getInputRangeLabel({ from: dateRange.from, to: dateRange.to, disableTime });
    }
    return null;
  };

  // One-time initialization to apply the default value if needed
  useEffect(() => {
    const startDateParam = searchParams.get(`${paramPrefix}start_date`);
    const endDateParam = searchParams.get(`${paramPrefix}end_date`);
    const customValueParam = searchParams.get(`${paramPrefix}date`);
    
    // If no URL parameters exist and component isn't initialized yet, set the default value
    if (!initialized.current && !customValueParam && !startDateParam && !endDateParam && defaultCustomValue) {
      initialized.current = true;
      const newParams = new URLSearchParams(searchParams);
      newParams.set(`${paramPrefix}date`, defaultCustomValue);
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams, paramPrefix, defaultCustomValue]);

  // Extract complex expressions to separate variables for dependency arrays
  const startDateParam = searchParams.get(`${paramPrefix}start_date`);
  const endDateParam = searchParams.get(`${paramPrefix}end_date`);
  const dateParam = searchParams.get(`${paramPrefix}date`);

  useEffect(() => {
    const startDateParam = searchParams.get(`${paramPrefix}start_date`);
    const endDateParam = searchParams.get(`${paramPrefix}end_date`);
    let customValueParam = searchParams.get(`${paramPrefix}date`);

    // Only update if the relevant params have changed
    if (
      startDateParam === lastStartDateParam.current &&
      endDateParam === lastEndDateParam.current &&
      customValueParam === lastCustomValueParam.current
    ) {
      return;
    }
    lastStartDateParam.current = startDateParam;
    lastEndDateParam.current = endDateParam;
    lastCustomValueParam.current = customValueParam;

    const newParams = new URLSearchParams(searchParams);

    // If a custom range is present and valid, use it and clear start/end params
    if (customValueParam && getCustomRange(customValueParam, quickRanges)) {
      newParams.delete(`${paramPrefix}start_date`);
      newParams.delete(`${paramPrefix}end_date`);
      setSearchParams(newParams);
      setCustomValue(customValueParam);
      const range =
        getCustomRange(customValueParam, quickRanges)?.getRange?.() ||
        getDateRange(getCustomRange(customValueParam, quickRanges));
      setDateRange(range);
      return;
    }

    // If a custom range is present and valid, use it and clear date param
    if (startDateParam && endDateParam) {
      newParams.delete(`${paramPrefix}date`);
      setSearchParams(newParams);
      setCustomValue(null);
      const from = new Date(decodeURIComponent(startDateParam));
      const to = new Date(decodeURIComponent(endDateParam));
      if (from < to && !isNaN(from.getTime()) && !isNaN(to.getTime())) {
        setDateRange({ from, to });
        return;
      }
    }

    // If nothing is present, set the default quick value if provided
    if (!customValueParam && !startDateParam && !endDateParam && defaultCustomValue) {
      newParams.set(`${paramPrefix}date`, defaultCustomValue);
      newParams.delete(`${paramPrefix}start_date`);
      newParams.delete(`${paramPrefix}end_date`);
      setSearchParams(newParams);
      setCustomValue(defaultCustomValue);
      const range =
        getCustomRange(defaultCustomValue, quickRanges)?.getRange?.() ||
        getDateRange(getCustomRange(defaultCustomValue, quickRanges));
      setDateRange(range);
      return;
    }

    // If nothing is present and no default, clear everything
    setCustomValue(null);
    setDateRange(undefined);
  }, [
    startDateParam,
    endDateParam,
    dateParam,
    quickRanges,
    paramPrefix,
    defaultCustomValue,
    searchParams,
    setSearchParams
  ]);

  useEffect(() => {
    if (value && (value.from !== dateRange?.from || value.to !== dateRange?.to)) {
      setDateRange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (dateRange) {
      applyFilter(dateRange);
    }
  }, [dateRange, applyFilter]);

  const dateRangeValue = formatDateRangeValue();

  const handleDateRangeChange = (range: DateRange, customValue?: string) => {
    setOpened(false);

    const newParams = new URLSearchParams(searchParams);
    if (customValue) {
      newParams.set(`${paramPrefix}date`, customValue);
      newParams.delete(`${paramPrefix}start_date`);
      newParams.delete(`${paramPrefix}end_date`);
    } else if (range.from && range.to) {
      newParams.set(`${paramPrefix}start_date`, formatUrlDateWithTimezone(range.from));
      newParams.set(`${paramPrefix}end_date`, formatUrlDateWithTimezone(range.to));
      newParams.delete(`${paramPrefix}date`);
    }
    setSearchParams(newParams);
  };

  return (
    <Box>
    <Popover
      radius="0.375rem"
      position="bottom-start"
      opened={opened}
      closeOnClickOutside={false}
    >
      <Popover.Target>
        <Input
          radius="0.375rem"
          type="button"
          component="button"
          onClick={() => setOpened(true)}
          style={{ marginBottom: 0 }}
          rightSection={<IconCalendar stroke={1} />}
          name="from"
          w="100%"
          pointer
          styles={{
            input: {
              height: '2.75rem',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            },
            section: {
              width: '2.5rem',
            },
          }}
        >
          {dateRangeValue ? (
            dateRangeValue
          ) : (
            <Input.Placeholder>Select date range</Input.Placeholder>
          )}
        </Input>
      </Popover.Target>
      <DateRangeDropdown
        onApply={handleDateRangeChange}
        onClose={() => setOpened(false)}
        range={dateRange}
        quickRanges={quickRanges}
        maxDate={maxDate}
        hideQuickSearch={hideQuickSearch}
        disableTime={disableTime}
      />
    </Popover>
    </Box>
  );
};

export default DateRangeFilter;
